import type { ForecastPreviewDataPointDTO } from '@/src/api/types/procurement'
import { FlexCol, FlexRow } from '@/src/components'
import { BaseText } from '@/src/components/BaseText'
import type { GranularityEnum } from '@/src/enums/granularity.enum'
import { getPreviewColor } from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/preview-graph/LoadPlanningPreview.utils'
import {
  type LoadPlanningPreviewCurveDataPointValueProperties,
  getEnergyUnitType,
  mapLoadPlanningUnitText,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { formatPortfolioTooltipTimestamp } from '@/src/features/procurement-and-markets/portfolio/components/diagram/utils/portfolioDiagram.utils'
import { appColors } from '@/src/theme'
import { formatMeasurement } from '@/src/utils/formatMeasurement'
import { Box } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import type { TooltipProps } from 'recharts'

interface LoadPlannningPreviewGraphTooltipProps extends TooltipProps<number, string> {
  granularity: GranularityEnum
}

export const LoadPlannningPreviewGraphTooltip = ({ active, payload, granularity }: LoadPlannningPreviewGraphTooltipProps) => {
  const { t } = useTranslation()

  if (!active || !payload) {
    return null
  }
  const [bar] = payload

  // I have to do casting since internally the payload is stored as `any` type and no way to define the payload type through the generics
  const dataPoint = bar?.payload as ForecastPreviewDataPointDTO | undefined

  if (!dataPoint) {
    return null
  }

  return (
    <FlexCol
      borderRadius={8}
      borderColor='gray.100'
      borderWidth='2px'
      className='custom-tooltip'
      backgroundColor='white'
      padding={3}
      opacity={'0.95'}
      width={'300px'}
      gap='4px'
    >
      <BaseText color={appColors.secondaryForeground}>{formatPortfolioTooltipTimestamp(dataPoint.timestampStart, granularity, t)}</BaseText>
      <TooltipRow keyName={'draftValue'} data={dataPoint} granularity={granularity} />
      <TooltipRow keyName={'previewValue'} data={dataPoint} granularity={granularity} />
    </FlexCol>
  )
}

const TOOLTIP_LABEL_MAP: Record<keyof LoadPlanningPreviewCurveDataPointValueProperties, string> = {
  draftValue: 'components.procurement.forecasting.graph.preview_adjusted_load',
  previewValue: 'components.procurement.forecasting.graph.preview_preview_load',
}

const TooltipRow = ({
  keyName,
  data,
  granularity,
}: {
  keyName: keyof LoadPlanningPreviewCurveDataPointValueProperties
  data?: ForecastPreviewDataPointDTO
  granularity: GranularityEnum
}) => {
  const { t } = useTranslation()

  if (!data?.[keyName]) return null
  const unitProperty = getEnergyUnitType(granularity)

  const valueData = data[keyName]
  if (!valueData) return null

  const amountInfo = valueData[unitProperty]

  return (
    <FlexRow justifyContent={'space-between'} alignItems={'center'}>
      <FlexRow alignItems={'center'} gap={'4px'}>
        <Box w={'12px'} h={'12px'} bg={getPreviewColor(keyName)} borderRadius={'4px'} />
        <BaseText>{t(TOOLTIP_LABEL_MAP[keyName])}</BaseText>
      </FlexRow>
      <BaseText fontWeight={116}>{formatMeasurement(amountInfo.amount, mapLoadPlanningUnitText(amountInfo.unit) ?? 'kWh')}</BaseText>
    </FlexRow>
  )
}
